if (!customElements.get('video-stories-carousel')) {
  customElements.define('video-stories-carousel', class VideoStoriesCarousel extends HTMLElement {
    constructor() {
      super();
      this.stage = this.querySelector('.video-stories__stage');
      this.slides = Array.from(this.querySelectorAll('.video-stories__slide'));
      this.dots = Array.from(this.querySelectorAll('.video-stories__dot'));
      this.prevBtn = this.querySelector('.video-stories__arrow--prev');
      this.nextBtn = this.querySelector('.video-stories__arrow--next');

      if (this.slides.length === 0) return;

      this.current = 0;
      this.animating = false;
      this.muted = true;
      this.autoplay = this.dataset.autoplay === 'true';
      this.loopVideo = this.dataset.loopVideo === 'true';
      this.showControls = this.dataset.showControls === 'true';
      this.autoAdvance = this.dataset.autoAdvance === 'true';
      this.touchX = 0;
      this.touchDX = 0;

      this.setup();
    }

    setup() {
      this.bind();
      this.layout();
      this.goTo(0, false);

      this._onResize = () => { this.layout(); this.positionSlides(false); };
      window.addEventListener('resize', this._onResize);
    }

    /**
     * Layout: compute sizes based on container width.
     * Center card: ~30% of container (min 240, max 360)
     * Side cards:  ~45% of center card size
     * On mobile:   center ~62%, sides ~35% of center
     */
    layout() {
      const w = this.offsetWidth;
      let centerW, sideRatio;

      if (w < 750) {
        // Mobile
        centerW = Math.round(w * 0.58);
        sideRatio = 0.38;
      } else if (w < 990) {
        // Tablet
        centerW = Math.min(320, Math.round(w * 0.32));
        sideRatio = 0.45;
      } else {
        // Desktop
        centerW = Math.min(360, Math.round(w * 0.26));
        sideRatio = 0.45;
      }

      this.centerW = centerW;
      this.sideW = Math.round(centerW * sideRatio);

      // Set stage height based on center card aspect ratio (9:16)
      const centerH = Math.round(centerW * 16 / 9);
      this.stage.style.height = centerH + 'px';
    }

    bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.dots.forEach(d => d.addEventListener('click', () => this.goTo(+d.dataset.index)));
      this.slides.forEach((s, i) => s.addEventListener('click', () => { if (i !== this.current) this.goTo(i); }));

      this.addEventListener('touchstart', e => { this.touchX = e.touches[0].clientX; this.touchDX = 0; }, { passive: true });
      this.addEventListener('touchmove', e => { this.touchDX = e.touches[0].clientX - this.touchX; }, { passive: true });
      this.addEventListener('touchend', () => { if (Math.abs(this.touchDX) > 40) { this.touchDX < 0 ? this.next() : this.prev(); } }, { passive: true });

      if (this.showControls) {
        this.querySelectorAll('.video-stories__btn--play').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); this.togglePlay(); }));
        this.querySelectorAll('.video-stories__btn--mute').forEach(b => b.addEventListener('click', e => { e.stopPropagation(); this.toggleMute(); }));
      }

      this.slides.forEach((s, i) => {
        const v = s.querySelector('video');
        if (v) v.addEventListener('ended', () => { if (i === this.current && this.autoAdvance && !this.loopVideo) this.next(); });
      });

      this.addEventListener('keydown', e => { if (e.key === 'ArrowLeft') this.prev(); if (e.key === 'ArrowRight') this.next(); });
    }

    prev() { this.goTo(this.current <= 0 ? this.slides.length - 1 : this.current - 1); }
    next() { this.goTo(this.current >= this.slides.length - 1 ? 0 : this.current + 1); }

    goTo(i, anim = true) {
      if (this.animating && anim) return;
      if (anim) this.animating = true;
      this.current = i;

      this.positionSlides(anim);
      this.dotStates();
      this.videos();

      if (anim) setTimeout(() => { this.animating = false; }, 550);
    }

    /**
     * Position the 3 visible slides (left, center, right) explicitly.
     * All other slides are hidden.
     */
    positionSlides(anim) {
      const len = this.slides.length;
      const leftIdx = this.current === 0 ? len - 1 : this.current - 1;
      const rightIdx = this.current === len - 1 ? 0 : this.current + 1;
      const stageW = this.stage.offsetWidth;
      const stageH = this.stage.offsetHeight;

      const cw = this.centerW;
      const sw = this.sideW;
      const sh = Math.round(sw * 16 / 9); // side height based on 9:16

      // Center card position
      const cx = (stageW - cw) / 2;

      // Side cards: peeking from behind center, vertically centered
      // Gap between center edge and side card edge
      const sideGap = Math.round(stageW < 750 ? 6 : 12);
      const leftX = cx - sw - sideGap;
      const rightX = cx + cw + sideGap;
      const sideTop = (stageH - sh) / 2;

      // Arrow positions: between center and side cards
      const arrowPrevX = cx - sideGap / 2 - 20; // 20 = half arrow width
      const arrowNextX = cx + cw + sideGap / 2 - 20;

      this.slides.forEach((slide, i) => {
        const t = anim ? 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
        slide.style.transition = t;

        // Reset classes
        slide.classList.remove('is-center', 'is-left', 'is-right');

        if (i === this.current) {
          // CENTER
          slide.classList.add('is-center');
          slide.style.width = cw + 'px';
          slide.style.left = cx + 'px';
          slide.style.top = '0';
          slide.style.position = 'absolute';
          slide.style.opacity = '1';
          slide.style.filter = 'none';
          slide.style.zIndex = '3';
          slide.style.pointerEvents = 'auto';
          slide.style.cursor = 'default';
        } else if (i === leftIdx) {
          // LEFT
          slide.classList.add('is-left');
          slide.style.width = sw + 'px';
          slide.style.left = Math.max(leftX, -sw * 0.3) + 'px';
          slide.style.top = sideTop + 'px';
          slide.style.position = 'absolute';
          slide.style.opacity = '0.55';
          slide.style.filter = 'brightness(0.85)';
          slide.style.zIndex = '2';
          slide.style.pointerEvents = 'auto';
          slide.style.cursor = 'pointer';
        } else if (i === rightIdx) {
          // RIGHT
          slide.classList.add('is-right');
          slide.style.width = sw + 'px';
          slide.style.left = Math.min(rightX, stageW - sw * 0.7) + 'px';
          slide.style.top = sideTop + 'px';
          slide.style.position = 'absolute';
          slide.style.opacity = '0.55';
          slide.style.filter = 'brightness(0.85)';
          slide.style.zIndex = '2';
          slide.style.pointerEvents = 'auto';
          slide.style.cursor = 'pointer';
        } else {
          // HIDDEN
          slide.style.width = sw + 'px';
          slide.style.left = (stageW / 2 - sw / 2) + 'px';
          slide.style.top = sideTop + 'px';
          slide.style.position = 'absolute';
          slide.style.opacity = '0';
          slide.style.filter = 'brightness(0.7)';
          slide.style.zIndex = '1';
          slide.style.pointerEvents = 'none';
          slide.style.cursor = 'default';
        }
      });

      // Position arrows
      if (this.prevBtn) {
        this.prevBtn.style.left = Math.max(4, arrowPrevX) + 'px';
      }
      if (this.nextBtn) {
        this.nextBtn.style.left = arrowNextX + 'px';
        this.nextBtn.style.right = 'auto';
      }
    }

    dotStates() {
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.current));
    }

    videos() {
      this.slides.forEach((s, i) => {
        const v = s.querySelector('video');
        if (!v) return;
        if (i === this.current && this.autoplay) {
          v.muted = this.muted;
          v.play().catch(() => {});
          s.classList.remove('is-paused');
          this.playIcon(s, true);
        } else {
          v.pause();
          v.currentTime = 0;
        }
      });
    }

    togglePlay() {
      const s = this.slides[this.current];
      const v = s.querySelector('video');
      if (!v) return;
      if (v.paused) {
        v.play().catch(() => {});
        s.classList.remove('is-paused');
        this.playIcon(s, true);
      } else {
        v.pause();
        s.classList.add('is-paused');
        this.playIcon(s, false);
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      this.slides.forEach(s => { const v = s.querySelector('video'); if (v) v.muted = this.muted; });
      this.querySelectorAll('.video-stories__btn--mute').forEach(b => {
        b.querySelector('.icon-muted').style.display = this.muted ? 'block' : 'none';
        b.querySelector('.icon-unmuted').style.display = this.muted ? 'none' : 'block';
      });
    }

    playIcon(s, playing) {
      const b = s.querySelector('.video-stories__btn--play');
      if (!b) return;
      b.querySelector('.icon-pause').style.display = playing ? 'block' : 'none';
      b.querySelector('.icon-play').style.display = playing ? 'none' : 'block';
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }
  });
}
